import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { AttendanceAdjustmentDialog } from "@/components/hr/AttendanceAdjustmentDialog";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import PermissionDenied from "@/components/PermissionDenied";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  attendanceAdjustmentsApiErrorMessage,
  deleteAttendanceAdjustment,
  fetchAttendanceAdjustments,
  type AttendanceAdjustmentRow,
  type AttendanceAdjustmentsPaginatedMeta,
} from "@/lib/hr/attendanceAdjustmentsStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  attendanceModeLabel,
  listWorkPolicies,
  syncWorkPoliciesFromApi,
} from "@/lib/hr/workPoliciesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, endOfMonth, format, isAfter, isValid, parseISO, startOfMonth } from "date-fns";
import {
  CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ClipboardList,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  User,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

type AppliedFilters = {
  dateFrom: string;
  dateTo: string;
  employeeId: string;
  page: number;
};

type AdjustDialogTarget = {
  employeeIds: string[];
  employeeName: string | null;
  adjustmentDates: string[];
  enableSplitOt: boolean;
  mode: "add" | "edit";
};

const TABLE_COLS = 8;

function truthyRuleValue(value: string | null | undefined): boolean {
  const v = (value || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

function employeeDisplayName(employee: EmployeeRecord): string {
  return (employee.fullName || `${employee.firstName} ${employee.lastName}`).trim() || employee.id;
}

function employeeLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

/** Work policy rule `enable_split_ot` → Start/End OT adjustment UI. */
function policyEnableSplitOt(policyId: string): boolean {
  const id = (policyId || "").trim();
  if (!id) return false;
  const policy = listWorkPolicies().find((p) => String(p.id) === id);
  const fromPolicy = policy?.attendanceRules?.find((r) => r.ruleKey === "enable_split_ot");
  return fromPolicy ? truthyRuleValue(fromPolicy.ruleValue) : false;
}

function parseDate(iso: string): Date | undefined {
  if (!iso.trim()) return undefined;
  const d = parseISO(iso);
  return isValid(d) ? d : undefined;
}

function formatDisplayDate(iso: string): string {
  const trimmed = (iso || "").trim().slice(0, 10);
  if (!trimmed) return "—";
  const d = parseDate(trimmed);
  return d ? format(d, "yyyy-MM-dd") : trimmed;
}

function currentMonthRange(): { from: string; to: string } {
  const d = new Date();
  return {
    from: format(startOfMonth(d), "yyyy-MM-dd"),
    to: format(endOfMonth(d), "yyyy-MM-dd"),
  };
}

function employeeDisplayFromRow(r: AttendanceAdjustmentRow): string {
  const name = (r.employeeName || "").trim();
  const code = (r.employeeCode || "").trim();
  if (code && name) return `${code} · ${name}`;
  return name || code || "—";
}

/** Match Daily Attendance `h:mm` style. */
function formatHoursMinutes(decimalHours: number): string {
  if (!Number.isFinite(decimalHours) || decimalHours <= 0) return "—";
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function signedAdjustmentDisplay(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes === 0) return "—";
  const body = formatHoursMinutes(Math.abs(minutes) / 60);
  if (body === "—") return "—";
  return minutes < 0 ? `−${body}` : `+${body}`;
}

function TypeBadges({ row }: { row: AttendanceAdjustmentRow }) {
  const showAdd = row.hasAdd;
  const showDeduct = row.hasDeduct;
  if (!showAdd && !showDeduct) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center justify-center gap-1">
      {showAdd ? (
        <span
          className="inline-flex items-center justify-center min-w-[1.75rem] text-sm font-bold leading-none px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
          title="Add"
        >
          +
        </span>
      ) : null}
      {showDeduct ? (
        <span
          className="inline-flex items-center justify-center min-w-[1.75rem] text-sm font-bold leading-none px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-800 dark:text-rose-300"
          title="Deduct"
        >
          −
        </span>
      ) : null}
    </span>
  );
}

/** Edit modal: use the saved row shape (split OT vs general OT). */
function rowIsSplitOt(r: AttendanceAdjustmentRow): boolean {
  if (r.isSplit) return true;
  return r.minutes == null && ((r.startOtMinutes ?? 0) > 0 || (r.endOtMinutes ?? 0) > 0);
}

export default function AttendanceAdjustments() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "attendance_adjustment_can_view_any") || can(user, "attendance_daily_can_view_any");
  const canCreate =
    can(user, "attendance_adjustment_can_create") ||
    can(user, "attendance_daily_can_process_from_punches") ||
    can(user, "attendance_daily_can_process_current_month");
  const canDelete =
    can(user, "attendance_adjustment_can_delete") ||
    can(user, "attendance_daily_can_process_from_punches") ||
    can(user, "attendance_daily_can_process_current_month");
  const [isForbidden, setIsForbidden] = useState(false);

  const [rows, setRows] = useState<AttendanceAdjustmentRow[]>([]);
  const [meta, setMeta] = useState<AttendanceAdjustmentsPaginatedMeta | null>(null);
  const [loading, setLoading] = useState(false);

  const currentMonth = useMemo(() => currentMonthRange(), []);
  const [dateFrom, setDateFrom] = useState(() => currentMonth.from);
  const [dateTo, setDateTo] = useState(() => currentMonth.to);
  const [employeeId, setEmployeeId] = useState("");
  const [applied, setApplied] = useState<AppliedFilters | null>(null);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);
  const workPolicies = useMemo(() => listWorkPolicies().filter((p) => p.isActive !== false), [empTick]);

  const [deleteTarget, setDeleteTarget] = useState<AttendanceAdjustmentRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<AdjustDialogTarget | null>(null);
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [addPolicyId, setAddPolicyId] = useState("");
  const [addEmployeeIds, setAddEmployeeIds] = useState<string[]>([]);
  const [addEmployeeSearch, setAddEmployeeSearch] = useState("");
  const [addDates, setAddDates] = useState<Date[]>([]);
  const [addDatePickerOpen, setAddDatePickerOpen] = useState(false);
  const [addEmployeePickerOpen, setAddEmployeePickerOpen] = useState(false);

  const todayEnd = useMemo(() => endOfDay(new Date()), []);
  const addSelectedEmployeeIdSet = useMemo(() => new Set(addEmployeeIds), [addEmployeeIds]);
  const addDatesSorted = useMemo(
    () =>
      [...addDates]
        .sort((a, b) => a.getTime() - b.getTime())
        .map((d) => format(d, "yyyy-MM-dd")),
    [addDates],
  );
  const addPolicyEmployees = useMemo(() => {
    const pid = addPolicyId.trim();
    if (!pid) return [];
    return activeEmployees.filter((e) => String(e.workPolicyId || "").trim() === pid);
  }, [activeEmployees, addPolicyId]);
  const filteredAddEmployees = useMemo(() => {
    const q = addEmployeeSearch.trim().toLowerCase();
    if (!q) return addPolicyEmployees;
    return addPolicyEmployees.filter((e) => {
      const name = employeeDisplayName(e).toLowerCase();
      const code = (e.employeeCode ?? "").toLowerCase();
      const dept = (e.departmentName ?? "").toLowerCase();
      return (
        name.includes(q) ||
        code.includes(q) ||
        dept.includes(q) ||
        e.id.toLowerCase().includes(q)
      );
    });
  }, [addPolicyEmployees, addEmployeeSearch]);

  const selectedAddPolicy = useMemo(
    () => workPolicies.find((p) => String(p.id) === addPolicyId.trim()) ?? null,
    [workPolicies, addPolicyId],
  );
  const selectedAddPolicySplitOt = addPolicyId.trim() ? policyEnableSplitOt(addPolicyId) : false;
  const addSelectionReady =
    Boolean(addPolicyId.trim()) && addEmployeeIds.length > 0 && addDatesSorted.length > 0;
  const addCombinationCount = addEmployeeIds.length * addDatesSorted.length;

  const load = useCallback(
    async (filters: AppliedFilters) => {
      if (!canView) {
        setIsForbidden(true);
        return;
      }
      setLoading(true);
      try {
        const result = await fetchAttendanceAdjustments({
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          employeeId: filters.employeeId || undefined,
          page: filters.page,
          perPage: 25,
        });
        setRows(result.rows);
        setMeta(result.meta);
        setIsForbidden(false);
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
        } else {
          toast.error(attendanceAdjustmentsApiErrorMessage(e));
        }
      } finally {
        setLoading(false);
      }
    },
    [canView],
  );

  useEffect(() => {
    setUser(getStoredAuthUser());
  }, []);

  useEffect(() => {
    void Promise.all([syncEmployeesFromApi(), syncWorkPoliciesFromApi()])
      .then(() => setEmpTick((t) => t + 1))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!applied) return;
    void load(applied);
  }, [applied, load]);

  const applyFilters = () => {
    setApplied({
      dateFrom,
      dateTo,
      employeeId,
      page: 1,
    });
  };

  const goToPage = (nextPage: number) => {
    setApplied((prev) => (prev ? { ...prev, page: nextPage } : prev));
  };

  const openEdit = (r: AttendanceAdjustmentRow) => {
    setAdjustTarget({
      employeeIds: [r.employeeId],
      employeeName: r.employeeName,
      adjustmentDates: [r.adjustmentDate],
      enableSplitOt: rowIsSplitOt(r),
      mode: "edit",
    });
  };

  const openAddPicker = () => {
    setAddPolicyId("");
    setAddEmployeeIds([]);
    setAddEmployeeSearch("");
    setAddDates([]);
    setAddDatePickerOpen(false);
    setAddEmployeePickerOpen(false);
    setAddPickerOpen(true);
  };

  const toggleAddEmployee = useCallback((id: string) => {
    setAddEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const continueAddAdjustment = () => {
    if (!addPolicyId.trim()) {
      toast.error("Select a work policy.");
      return;
    }
    if (addEmployeeIds.length < 1) {
      toast.error("Select at least one employee.");
      return;
    }
    if (addDatesSorted.length < 1) {
      toast.error("Select at least one date.");
      return;
    }
    const label =
      addEmployeeIds.length === 1
        ? (() => {
            const emp = employees.find((e) => e.id === addEmployeeIds[0]);
            return emp ? employeeDisplayName(emp) : null;
          })()
        : `${addEmployeeIds.length} employees selected`;
    setAddPickerOpen(false);
    setAdjustTarget({
      employeeIds: [...addEmployeeIds],
      employeeName: label,
      adjustmentDates: addDatesSorted,
      enableSplitOt: policyEnableSplitOt(addPolicyId),
      mode: "add",
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !applied) return;
    setDeleting(true);
    try {
      const { message } = await deleteAttendanceAdjustment(deleteTarget.id);
      toast.success(message);
      setDeleteTarget(null);
      await load(applied);
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  if (!canView || isForbidden) {
    return (
      <PermissionDenied
        title="Attendance Adjustment access required"
        message="You don’t have permission to view attendance adjustments. Ask an administrator to grant attendance_adjustment_can_view_any."
      />
    );
  }

  return (
    <Layout title="Attendance Adjustment">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Attendance Adjustment</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Attendance time and OT adjustments recorded from daily attendance.
            </p>
          </div>
          {canCreate ? (
            <Button type="button" variant="premium" className="gap-2 shrink-0" onClick={openAddPicker}>
              <Plus className="h-4 w-4" />
              Add Adjustment
            </Button>
          ) : null}
        </div>

        <div className="glass-card p-6 space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
              <div className="space-y-2">
                <Label htmlFor="adj-from" className="text-xs text-muted-foreground">
                  From
                </Label>
                <DatePartsPicker id="adj-from" value={dateFrom} onChange={setDateFrom} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adj-to" className="text-xs text-muted-foreground">
                  To
                </Label>
                <DatePartsPicker id="adj-to" value={dateTo} onChange={setDateTo} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Employee</Label>
                <Popover
                  open={employeePickerOpen}
                  onOpenChange={(next) => {
                    setEmployeePickerOpen(next);
                    if (next) setEmployeePickerCycle((c) => c + 1);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={employeePickerOpen}
                      className={cn(
                        "glass-input h-10 w-full justify-between px-3 font-normal",
                        !employeeId && "text-muted-foreground",
                      )}
                    >
                      {employeeId ? (
                        <span className="flex items-center gap-2 truncate">
                          {(() => {
                            const sel = employees.find((e) => e.id === employeeId);
                            if (!sel) return "Select employee…";
                            return (
                              <>
                                <Avatar className="h-5 w-5 text-[8px]">
                                  {sel.photoDataUrl ? (
                                    <AvatarImage src={sel.photoDataUrl} alt={employeeDisplayName(sel)} />
                                  ) : null}
                                  <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                    {employeeInitials(sel)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate">{employeeLabel(sel)}</span>
                              </>
                            );
                          })()}
                        </span>
                      ) : (
                        <span className="truncate text-left text-sm text-foreground/80">All employees</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    sideOffset={4}
                  >
                    <Command key={employeePickerCycle} shouldFilter>
                      <CommandInput placeholder="Search name, code or department…" className="h-9" />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__all__ all employees"
                            onSelect={() => {
                              setEmployeeId("");
                              setEmployeePickerOpen(false);
                            }}
                            className="gap-2 text-sm"
                          >
                            <Check className={cn("h-3.5 w-3.5 shrink-0", !employeeId ? "opacity-100" : "opacity-0")} />
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>All employees</span>
                          </CommandItem>
                          {activeEmployees.map((e) => {
                            const label = employeeLabel(e);
                            const subtitle = employeeSubtitle(e);
                            const selected = employeeId === e.id;
                            const searchValue = [
                              label,
                              e.employeeCode,
                              e.departmentName,
                              e.designationName,
                              e.id,
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <CommandItem
                                key={e.id}
                                value={searchValue}
                                onSelect={() => {
                                  setEmployeeId(e.id);
                                  setEmployeePickerOpen(false);
                                }}
                                className="gap-2 text-sm"
                              >
                                <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                  {e.photoDataUrl ? (
                                    <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                  ) : null}
                                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                    {employeeInitials(e)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col min-w-0">
                                  <span className="truncate text-sm">{label}</span>
                                  {subtitle ? (
                                    <span className="truncate text-[10px] text-muted-foreground">{subtitle}</span>
                                  ) : null}
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <Button
              type="button"
              variant="premium"
              className="shrink-0 gap-2"
              onClick={applyFilters}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </Button>
          </div>

          <div
            className={cn(
              "overflow-x-auto rounded-lg border border-border/50",
              loading && rows.length > 0 && "opacity-60 pointer-events-none",
            )}
          >
            <table className="w-full min-w-[56rem] text-sm">
              <thead>
                <tr className="border-b border-border/50 bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium py-1.5 px-2.5">Date</th>
                  <th className="text-left font-medium py-1.5 px-2.5">Employee</th>
                  <th className="text-center font-medium py-1.5 px-2.5">Type</th>
                  <th className="text-right font-medium py-1.5 px-2.5" title="OT Adjustment">
                    OT Adj
                  </th>
                  <th className="text-right font-medium py-1.5 px-2.5" title="Shift Start OT Adjustment">
                    SAOT
                  </th>
                  <th className="text-right font-medium py-1.5 px-2.5" title="Shift End OT Adjustment">
                    EAOT
                  </th>
                  <th className="text-left font-medium py-1.5 px-2.5">Reason</th>
                  <th className="text-right font-medium py-1.5 px-2.5 w-[7.5rem]"> </th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLS} className="py-10 text-center text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading adjustments…
                      </span>
                    </td>
                  </tr>
                ) : !applied ? (
                  <tr>
                    <td colSpan={TABLE_COLS} className="py-8 text-center text-muted-foreground">
                      Set filters and click Search to load adjustments.
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLS} className="py-8 text-center text-muted-foreground">
                      No attendance adjustments for this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={`${r.employeeId}-${r.adjustmentDate}-${r.id}`} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                      <td className="py-1 px-2.5 tabular-nums text-foreground">{formatDisplayDate(r.adjustmentDate)}</td>
                      <td className="py-1 px-2.5 text-foreground">{employeeDisplayFromRow(r)}</td>
                      <td className="py-1 px-2.5 text-center">
                        <TypeBadges row={r} />
                      </td>
                      <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                        {signedAdjustmentDisplay(r.netMinutes)}
                      </td>
                      <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                        {signedAdjustmentDisplay(r.netStartOtMinutes)}
                      </td>
                      <td className="py-1 px-2.5 text-right tabular-nums text-muted-foreground">
                        {signedAdjustmentDisplay(r.netEndOtMinutes)}
                      </td>
                      <td
                        className="py-1 px-2.5 text-muted-foreground max-w-[14rem] truncate"
                        title={r.reason ?? undefined}
                      >
                        {r.reason?.trim() || "—"}
                      </td>
                      <td className="py-1 px-1.5 text-right">
                        <div className="inline-flex items-center justify-end gap-0.5">
                          {canCreate ? (
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  aria-label="Edit adjustment"
                                  onClick={() => openEdit(r)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Edit adjustment</TooltipContent>
                            </Tooltip>
                          ) : null}
                          {canDelete ? (
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  aria-label="Delete adjustment"
                                  onClick={() => setDeleteTarget(r)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Delete adjustment</TooltipContent>
                            </Tooltip>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && meta.lastPage > 1 ? (
            <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>
                Page {meta.currentPage} of {meta.lastPage} ({meta.total} total)
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={meta.currentPage <= 1 || loading}
                  onClick={() => goToPage(Math.max(1, meta.currentPage - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={meta.currentPage >= meta.lastPage || loading}
                  onClick={() => goToPage(meta.currentPage + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : meta && meta.total > 0 ? (
            <div className="text-sm text-muted-foreground">{meta.total} total</div>
          ) : null}
        </div>
      </div>

      <Dialog
        open={addPickerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddPickerOpen(false);
            setAddEmployeePickerOpen(false);
            setAddDatePickerOpen(false);
            setAddEmployeeSearch("");
          }
        }}
      >
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
          <DialogHeader className="pb-3 border-b border-border/50">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-foreground">Add Adjustment</DialogTitle>
                <p className="text-xs text-muted-foreground">
                  Apply the same attendance adjustment to selected employees on selected dates.
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-xl border border-border/60 bg-muted/10 p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 pb-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-foreground">Work Policy:</span>
                  {selectedAddPolicy ? (
                    <span className="font-extrabold text-primary text-sm">{selectedAddPolicy.name}</span>
                  ) : (
                    <span className="text-muted-foreground">Select a policy to continue</span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Future dates are excluded. Only employees on this policy are listed.
                </p>
              </div>

              {selectedAddPolicy ? (
                <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                  <span className="text-[11px] font-semibold text-muted-foreground">Policy details:</span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background">
                    <span className="text-primary font-bold">{attendanceModeLabel(selectedAddPolicy.attendanceMode)}</span>
                    <span className="text-muted-foreground text-[10px] ml-1">mode</span>
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background">
                    <span className="text-primary font-bold">
                      {selectedAddPolicySplitOt ? "Start/End OT" : "General OT"}
                    </span>
                    <span className="text-muted-foreground text-[10px] ml-1">form</span>
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background">
                    <span className="text-primary font-bold">{addPolicyEmployees.length}</span>
                    <span className="text-muted-foreground text-[10px] ml-1">eligible</span>
                  </span>
                  {addSelectionReady ? (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-primary/30 bg-primary/10">
                      <span className="text-primary font-bold">{addCombinationCount}</span>
                      <span className="text-muted-foreground text-[10px] ml-1">
                        {addCombinationCount === 1 ? "record" : "records"}
                      </span>
                    </span>
                  ) : null}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Policy controls who appears in the employee list and whether Start/End OT fields are used.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div className="hidden md:grid md:grid-cols-[minmax(11rem,1.1fr)_minmax(12rem,1.3fr)_minmax(11rem,1.1fr)] gap-3 px-1">
                <Label className="text-xs font-bold text-foreground">Work Policy</Label>
                <Label className="text-xs font-bold text-foreground">Employees</Label>
                <Label className="text-xs font-bold text-foreground">Dates</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[minmax(11rem,1.1fr)_minmax(12rem,1.3fr)_minmax(11rem,1.1fr)] gap-3 items-end rounded-xl border border-border/60 p-3 md:border-0 md:p-0 md:rounded-none bg-background/50">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground md:sr-only">Work policy</Label>
                  <Select
                    value={addPolicyId || undefined}
                    onValueChange={(v) => {
                      setAddPolicyId(v);
                      setAddEmployeeIds([]);
                      setAddEmployeeSearch("");
                      setAddEmployeePickerOpen(false);
                    }}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Select policy" />
                    </SelectTrigger>
                    <SelectContent>
                      {workPolicies.length === 0 ? (
                        <SelectItem value="__none__" disabled>
                          No work policies found
                        </SelectItem>
                      ) : (
                        workPolicies.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.name}
                            {" — "}
                            {attendanceModeLabel(p.attendanceMode)}
                            {" · "}
                            {policyEnableSplitOt(String(p.id)) ? "Start/End OT" : "General OT"}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground md:sr-only">Employees</Label>
                  <Popover
                    modal
                    open={addEmployeePickerOpen}
                    onOpenChange={(next) => {
                      if (!addPolicyId.trim()) {
                        toast.error("Select a work policy first.");
                        return;
                      }
                      setAddEmployeePickerOpen(next);
                      if (next) setAddEmployeeSearch("");
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "glass-input h-10 w-full justify-between font-normal",
                          addEmployeeIds.length === 0 && "text-muted-foreground",
                        )}
                        disabled={!addPolicyId.trim()}
                      >
                        <span className="truncate">
                          {addEmployeeIds.length > 0
                            ? `${addEmployeeIds.length} employee${addEmployeeIds.length > 1 ? "s" : ""} selected`
                            : addPolicyId.trim()
                              ? "Search and select employees…"
                              : "Select a work policy first…"}
                        </span>
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                      avoidCollisions={false}
                    >
                      <div className="border-b border-border/50 p-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            autoFocus
                            className="h-9 pl-8"
                            placeholder="Search employees by name or code…"
                            value={addEmployeeSearch}
                            onChange={(e) => setAddEmployeeSearch(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="max-h-[220px] overflow-y-auto p-1">
                        {filteredAddEmployees.length === 0 ? (
                          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                            {addPolicyEmployees.length === 0
                              ? "No employees on this work policy."
                              : "No employees found."}
                          </p>
                        ) : (
                          filteredAddEmployees.map((e) => {
                            const selected = addSelectedEmployeeIdSet.has(e.id);
                            const label = employeeLabel(e);
                            const subtitle = employeeSubtitle(e);
                            return (
                              <button
                                key={e.id}
                                type="button"
                                onClick={() => toggleAddEmployee(e.id)}
                                className={cn(
                                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/60",
                                  selected && "bg-muted/40",
                                )}
                              >
                                <span
                                  className={cn(
                                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                    selected ? "border-primary/50 bg-primary/10" : "border-border",
                                  )}
                                >
                                  {selected ? <Check className="h-3 w-3 text-primary" /> : null}
                                </span>
                                <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                  {e.photoDataUrl ? (
                                    <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                  ) : null}
                                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                    {employeeInitials(e)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <span className="truncate">{label}</span>
                                  {subtitle ? (
                                    <span className="truncate text-[10px] text-muted-foreground">{subtitle}</span>
                                  ) : null}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground md:sr-only">Dates</Label>
                  <Popover modal open={addDatePickerOpen} onOpenChange={setAddDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "glass-input h-10 w-full justify-start text-left font-normal",
                          addDates.length === 0 && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                        <span className="truncate">
                          {addDatesSorted.length > 0
                            ? addDatesSorted.length === 1
                              ? addDatesSorted[0]
                              : `${addDatesSorted.length} dates selected`
                            : "Select dates"}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="z-[100] w-auto p-0"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <Calendar
                        mode="multiple"
                        selected={addDates}
                        onSelect={(dates) => {
                          const next = (dates ?? []).filter((d) => !isAfter(d, todayEnd));
                          setAddDates(next);
                        }}
                        disabled={(date) => isAfter(date, todayEnd)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t border-border/50 gap-2">
            <Button type="button" variant="outline" onClick={() => setAddPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={continueAddAdjustment}
              disabled={!addSelectionReady}
              className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {adjustTarget ? (
        <AttendanceAdjustmentDialog
          open={adjustTarget != null}
          onOpenChange={(open) => {
            if (!open) setAdjustTarget(null);
          }}
          employeeIds={adjustTarget.employeeIds}
          employeeName={adjustTarget.employeeName}
          adjustmentDates={adjustTarget.adjustmentDates}
          enableSplitOt={adjustTarget.enableSplitOt}
          mode={adjustTarget.mode}
          canCreate={canCreate}
          onChanged={() => {
            if (applied) void load(applied);
          }}
        />
      ) : null}

      <ConfirmAlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
        title="Delete attendance adjustment?"
        description={
          deleteTarget ? (
            <>
              Remove the attendance adjustment for{" "}
              <strong>{employeeDisplayFromRow(deleteTarget)}</strong> on{" "}
              {formatDisplayDate(deleteTarget.adjustmentDate)}?
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        onConfirm={() => {
          if (!deleting) void confirmDelete();
        }}
      />
    </Layout>
  );
}
